import React from 'react'
import { isNumber } from 'lodash'

import Text from '~/views/components/ui/Text'
import {
  CardBodyContainer,
  CardBodyContent,
  LineDivider,
  CardHeaderContainer,
  ContainerCard,
} from '~/views/components/ProjectOverview/components/ProjectOverviewCard'
import AccruedContent from './components/AccruedContent'
import ForecastedContent from './components/ForecastedContent'
import { useCurrentUserContext } from '~/views/contexts/CurrentUserContext'
import LocalizedCurrencyNumber from '~/views/components/currency/LocalizedCurrencyNumber'
import { useTheme } from '~/common/theme'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

type ProjectOverviewCostCardProps = {
  accruedCost?: number | null
  budgetedCost?: number | null
  forecastedCost?: number | null
  show?: boolean | null
  loading?: boolean | null
  projectHasTasks?: boolean
}

export default function ProjectOverviewCostCard(
  props: ProjectOverviewCostCardProps,
) {
  const {
    accruedCost,
    budgetedCost,
    show,
    forecastedCost,
    loading,
    projectHasTasks,
  } = props

  if (show !== true) {
    return null
  }

  return (
    <ContainerCard data-component-name={'cost-card'}>
      <CostCardHeader budgetedCost={budgetedCost} />

      <CostCardBody
        projectHasTasks={projectHasTasks}
        loading={loading === true}
        accruedCost={accruedCost}
        forecastedCost={forecastedCost}
        budgetedCost={budgetedCost}
      />
    </ContainerCard>
  )
}

type CostCardHeaderProps = {
  budgetedCost?: number | null
}

function CostCardHeader({ budgetedCost }: CostCardHeaderProps) {
  const [getCommonLabel] = useStaticLabels(['common'])

  const { currentUser } = useCurrentUserContext()
  const theme = useTheme()

  return (
    <CardHeaderContainer>
      <Text data-component-name={'cost-title'} weight={'bold'} size={'large'}>
        {getCommonLabel('costs')}
      </Text>

      {isNumber(budgetedCost) && (
        <span>
          <small
            data-component-name={'budgeted-cost-label'}
            style={{ marginRight: theme.spacing(2) }}
          >
            {getCommonLabel('budgeted')}
          </small>
          <span
            className={'font-bold'}
            data-component-name={'budgeted-cost-value'}
          >
            <LocalizedCurrencyNumber
              number={budgetedCost}
              currentUser={currentUser}
            />
          </span>
        </span>
      )}
    </CardHeaderContainer>
  )
}

type CostCardBodyProps = {
  accruedCost?: number | null
  forecastedCost?: number | null
  budgetedCost?: number | null
  loading: boolean
  projectHasTasks?: boolean
}

function CostCardBody({
  accruedCost,
  forecastedCost,
  budgetedCost,
  loading,
  projectHasTasks = true,
}: CostCardBodyProps) {
  return (
    <CardBodyContainer>
      <CardBodyContent>
        <AccruedContent accruedCost={accruedCost} loading={loading} />
        <LineDivider />
        <ForecastedContent
          projectHasTasks={projectHasTasks}
          forecastedCost={forecastedCost}
          budgetedCost={budgetedCost}
          loading={loading}
        />
      </CardBodyContent>
    </CardBodyContainer>
  )
}
